// Phase 12.1 BUG 4 — Command palette.
// Triggered by ⌘ K / Ctrl K (handled in /app/frontend/src/lib/shortcuts.js).
// Mounted once at App level so it works from any route.
// 2026-07-29 · CHG-067 verified — palette already lists the last 20
// analyses with fuzzy filter on title + prompt, Enter navigates. Data
// testids in place at 'command-palette-overlay' + 'command-palette'.
import { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { motion, AnimatePresence, useReducedMotion } from "framer-motion";
import { Search, ArrowRight } from "lucide-react";
import { isMacLike, getShortcuts } from "../lib/shortcuts";
import { requestGuideOpen, isAnalysisRoute } from "../lib/guideOpen";
import { requestShowValuesToggle } from "../lib/ShowValuesContext";
import { resetTour } from "../lib/guidedTour";

/** Build the canonical command list. Each command has:
 *   id, label, hint (shortcut hint string), run({ navigate, ...ctx })
 */
function buildCommands({ macLike }) {
    const mod = macLike ? "⌘" : "Ctrl";
    const sep = macLike ? " " : " + ";
    return [
        {
            id: "new-analysis",
            label: "New analysis",
            hint: `${mod}${sep}N`,
            keywords: ["new", "create", "analysis", "start", "run"],
            run: ({ navigate }) => navigate("/analyses/new"),
        },
        {
            id: "open-guide",
            label: "Ask the Guide",
            // Issue #172 finding 4 — the `${mod}${sep}J` hint is gone: no
            // handler for it exists anywhere (App.js notes the shortcut
            // was removed with Counsel), so it advertised a key combo
            // that did nothing.
            keywords: ["guide", "chat", "assistant", "ask", "ai"],
            // Only where an analysis — and therefore a drawer — exists.
            // The old entry was listed everywhere and silently navigated
            // the user to /analyses when it couldn't work.
            available: () => isAnalysisRoute(),
            run: () => {
                // Asks through the open contract instead of clicking
                // [data-testid="guide-open"], which only exists while the
                // drawer is CLOSED — so the old entry was a no-op once it
                // was already open.
                requestGuideOpen();
            },
        },
        {
            id: "toggle-values",
            label: "Toggle Show Values",
            keywords: ["values", "numbers", "hide", "reveal", "mask"],
            available: () => isAnalysisRoute(),
            // Issue #247 — this clicked `results-header-show-values`, a test
            // id nothing renders any more, so it did nothing.
            run: () => { requestShowValuesToggle(); },
        },
        {
            id: "copy-audit-link",
            label: "Copy audit link (current analysis)",
            keywords: ["audit", "share", "hmac", "link", "copy"],
            run: () => {
                const btn = document.querySelector('[data-testid="results-header-audit-link"]');
                if (btn) btn.click();
            },
        },
        {
            id: "toggle-voice",
            label: "Toggle voice input",
            hint: `${mod}${sep}M`,
            keywords: ["voice", "dictate", "microphone", "mic", "speech"],
            run: () => window.dispatchEvent(new CustomEvent("scope-toggle-voice")),
        },
        {
            id: "copy-permalink",
            label: "Copy permalink to this view",
            hint: `${mod}${sep}.`,
            keywords: ["copy", "permalink", "link", "url", "share"],
            run: () => {
                try {
                    navigator.clipboard?.writeText(window.location.href);
                } catch { /* noop */ }
            },
        },
        {
            id: "how-it-works",
            label: "How does this work?",
            hint: `${mod}${sep}/`,
            keywords: ["how", "help", "walkthrough", "tour", "guide", "intro"],
            run: ({ navigate }) => navigate("/how-it-works"),
        },
        // 2026-07-27 CHANGE 24 — "Open Calibration" command removed; /calibration deleted.
        {
            id: "open-analyses",
            label: "Open Analyses list",
            keywords: ["analyses", "list", "all", "history"],
            run: ({ navigate }) => navigate("/analyses"),
        },
        {
            id: "show-shortcuts",
            label: "Show all keyboard shortcuts",
            keywords: ["shortcut", "keyboard", "keymap", "hotkey", "commands"],
            isShortcutsView: true,
        },
        {
            id: "take-the-tour",
            label: "Take the tour",
            keywords: ["tour", "guided", "intro", "walkthrough", "joyride"],
            run: ({ navigate }) => {
                resetTour();
                navigate("/");
                setTimeout(() => {
                    window.dispatchEvent(new CustomEvent("scope-tour-restart"));
                }, 250);
            },
        },
    ];
}

function fuzzyMatch(query, label, keywords) {
    if (!query) return 1;
    const q = query.toLowerCase().trim();
    const hay = `${label} ${(keywords || []).join(" ")}`.toLowerCase();
    if (hay.includes(q)) return 2;
    // subsequence match
    let i = 0;
    for (const ch of hay) {
        if (ch === q[i]) i++;
        if (i >= q.length) return 1;
    }
    return 0;
}

export default function CommandPalette() {
    const navigate = useNavigate();
    const reduce = useReducedMotion();
    const [open, setOpen] = useState(false);
    const [query, setQuery] = useState("");
    const [activeIndex, setActiveIndex] = useState(0);
    const [view, setView] = useState("commands"); // "commands" | "shortcuts"
    const inputRef = useRef(null);
    const macLike = isMacLike();
    const allCommands = useMemo(() => buildCommands({ macLike }), [macLike]);

    const filtered = useMemo(() => {
        if (view === "shortcuts") return [];
        const scored = allCommands
            // Issue #172 finding 4 — commands may declare `available()`.
            // Previously every command was listed on every page, so
            // page-specific ones were offered where they could not work
            // and silently navigated the user away instead.
            .filter((c) => (typeof c.available === "function" ? c.available() : true))
            .map((c) => ({ c, s: fuzzyMatch(query, c.label, c.keywords) }))
            .filter((x) => x.s > 0);
        scored.sort((a, b) => b.s - a.s);
        return scored.map((x) => x.c);
    }, [allCommands, query, view]);

    // Open via global shortcut event
    useEffect(() => {
        const handler = () => {
            setOpen((prev) => !prev);
            setQuery("");
            setActiveIndex(0);
            setView("commands");
        };
        window.addEventListener("scope-shortcut:command-palette", handler);
        return () => window.removeEventListener("scope-shortcut:command-palette", handler);
    }, []);

    // Focus input when opened
    useEffect(() => {
        if (open) {
            setTimeout(() => inputRef.current?.focus(), 30);
        }
    }, [open]);

    // Esc closes, Up/Down navigate, Enter runs
    useEffect(() => {
        if (!open) return;
        const onKey = (e) => {
            if (e.key === "Escape") {
                e.preventDefault();
                if (view === "shortcuts") {
                    setView("commands");
                } else {
                    setOpen(false);
                }
                return;
            }
            if (view !== "commands") return;
            if (e.key === "ArrowDown") {
                e.preventDefault();
                setActiveIndex((i) => Math.min(i + 1, Math.max(0, filtered.length - 1)));
            } else if (e.key === "ArrowUp") {
                e.preventDefault();
                setActiveIndex((i) => Math.max(0, i - 1));
            } else if (e.key === "Enter") {
                e.preventDefault();
                const cmd = filtered[activeIndex];
                if (!cmd) return;
                if (cmd.isShortcutsView) {
                    setView("shortcuts");
                    return;
                }
                cmd.run({ navigate });
                setOpen(false);
            }
        };
        window.addEventListener("keydown", onKey);
        return () => window.removeEventListener("keydown", onKey);
    }, [open, view, filtered, activeIndex, navigate]);

    useEffect(() => { setActiveIndex(0); }, [query, view]);

    const fade = reduce ? {} : {
        initial: { opacity: 0, scale: 0.96 },
        animate: { opacity: 1, scale: 1 },
        exit: { opacity: 0, scale: 0.96 },
        transition: { duration: 0.18, ease: [0.22, 1, 0.36, 1] },
    };

    const shortcutHint = macLike ? "⌘ K" : "Ctrl K";
    const shortcutTable = getShortcuts();

    return (
        <AnimatePresence>
            {open && (
                <motion.div
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    exit={{ opacity: 0 }}
                    transition={{ duration: 0.12 }}
                    className="fixed inset-0 z-[7000] bg-ink/35 flex items-start justify-center pt-[15vh]"
                    onClick={() => setOpen(false)}
                    data-testid="command-palette-overlay"
                >
                    <motion.div
                        {...fade}
                        onClick={(e) => e.stopPropagation()}
                        className="bg-paper border border-ink/15 shadow-2xl w-[560px] max-w-[92vw] flex flex-col"
                        style={{ borderRadius: 8, maxHeight: 440 }}
                        data-testid="command-palette"
                        role="dialog"
                        aria-label="Command palette"
                    >
                        {/* Header / input */}
                        <div className="px-4 py-3 border-b border-ink/10 flex items-center gap-2">
                            <Search size={16} className="text-ink-soft flex-none" />
                            <input
                                ref={inputRef}
                                value={query}
                                onChange={(e) => setQuery(e.target.value)}
                                placeholder={view === "shortcuts" ? "Keyboard shortcuts" : "Type a command…"}
                                disabled={view === "shortcuts"}
                                data-testid="command-palette-input"
                                className="flex-1 bg-transparent text-[13px] text-ink placeholder:text-ink-soft/60 focus:outline-none"
                            />
                            <span
                                data-testid="command-palette-shortcut-hint"
                                className="font-mono text-[10px] uppercase tracking-[0.14em] text-ink-soft border border-ink/15 px-1.5 py-0.5"
                            >
                                {shortcutHint}
                            </span>
                        </div>

                        {/* Body */}
                        <div className="flex-1 overflow-y-auto" data-testid="command-palette-body">
                            {view === "commands" ? (
                                filtered.length === 0 ? (
                                    <div className="p-6 text-[13px] text-ink-soft italic text-center" data-testid="command-palette-empty">
                                        No matches.
                                    </div>
                                ) : (
                                    <ul role="listbox">
                                        {filtered.map((cmd, idx) => (
                                            <li
                                                key={cmd.id}
                                                role="option"
                                                aria-selected={idx === activeIndex}
                                                data-testid={`command-${cmd.id}`}
                                                onMouseEnter={() => setActiveIndex(idx)}
                                                onClick={() => {
                                                    if (cmd.isShortcutsView) {
                                                        setView("shortcuts");
                                                    } else {
                                                        cmd.run({ navigate });
                                                        setOpen(false);
                                                    }
                                                }}
                                                className={`px-4 py-2.5 flex items-center justify-between gap-3 cursor-pointer transition-colors ${
                                                    idx === activeIndex ? "bg-amber-soft/50" : "hover:bg-paper-soft"
                                                }`}
                                            >
                                                <span className="text-[13px] text-ink">{cmd.label}</span>
                                                <span className="flex items-center gap-2">
                                                    {cmd.hint && (
                                                        <span className="font-mono text-[10px] uppercase tracking-[0.14em] text-ink-soft">
                                                            {cmd.hint}
                                                        </span>
                                                    )}
                                                    <ArrowRight size={12} className="text-ink-soft/60" />
                                                </span>
                                            </li>
                                        ))}
                                    </ul>
                                )
                            ) : (
                                <table className="w-full text-[12.5px]" data-testid="command-palette-shortcuts">
                                    <tbody>
                                        {shortcutTable.map((s) => (
                                            <tr key={s.id} className="border-b border-ink/8">
                                                <td className="px-4 py-2 text-ink">{s.action}</td>
                                                <td className="px-4 py-2 font-mono text-ink-soft text-right">{s.label}</td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            )}
                        </div>

                        {/* Footer */}
                        <div className="px-4 py-2 border-t border-ink/10 flex items-center justify-between font-mono text-[10px] text-ink-soft tracking-[0.14em]">
                            <span data-testid="command-palette-footer-hints">
                                ↑↓ navigate · ↵ run · Esc close
                            </span>
                            {view === "shortcuts" && (
                                <button
                                    onClick={() => setView("commands")}
                                    className="hover:text-ink uppercase"
                                    data-testid="command-palette-back"
                                >
                                    ← back
                                </button>
                            )}
                        </div>
                    </motion.div>
                </motion.div>
            )}
        </AnimatePresence>
    );
}
